import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, XCircle, ArrowLeft, MapPin, Building2,
  TrendingUp, AlertCircle, FileCheck, Award, X
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import API from '../api';
import { useAuth } from '../auth/AuthContext';

// Module III A (district) / III B (state) — monitoring dashboard.
// Visually rich: stat cards + status pie + district/dept bars + drill-down table.
export default function StateDashboard({ scope = 'state' }) {
  const navigate = useNavigate();
  const isDistrictScope    = scope === 'district';
  const isDepartmentScope  = scope === 'department';

  const { user: userInfo, isReady: authReady, isAuthenticated } = useAuth();
  const userDistrictId   = userInfo?.district_id   ? Number(userInfo.district_id)   : null;
  const userDepartmentId = userInfo?.department_id ? Number(userInfo.department_id) : null;

  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // "Restored" filter: applications with a Work Completion Certificate issued.
  // Seeded when the user clicks the KPI cards.
  const [filterCompleted, setFilterCompleted] = useState(''); // '' | 'yes' | 'no'

  // KPI drill-down modal: { title, rows: enrichedApp[] } or null.
  const [drillModal, setDrillModal] = useState(null);

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    const safeGet = (url) =>
      API.get(url).then((r) => (Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => []);
    setLoading(true);
    Promise.all([
      safeGet('/applications'),
      safeGet('/tanks'),
      safeGet('/districts'),
      safeGet('/departments'),
    ]).then(([apps, tnks, dists, depts]) => {
      let scoped = apps;
      if (isDistrictScope && userDistrictId) {
        scoped = scoped.filter((a) => Number(a.district_id) === userDistrictId);
      }
      if (isDepartmentScope && userDepartmentId) {
        scoped = scoped.filter((a) => Number(a.department_id) === userDepartmentId);
      }
      setApplications(scoped);
      setTanks(tnks);
      setDistricts(dists);
      setDepartments(depts);
      setLoading(false);
    });
  }, [navigate, isDistrictScope, userDistrictId, isDepartmentScope, userDepartmentId, authReady, isAuthenticated]);

  const tankById       = useMemo(() => new Map(tanks.map((t) => [Number(t.tank_id), t])), [tanks]);
  const districtById   = useMemo(() => new Map(districts.map((d) => [Number(d.district_id), d])), [districts]);
  const departmentById = useMemo(() => new Map(departments.map((d) => [Number(d.department_id), d])), [departments]);

  const districtName = useMemo(() => {
    if (!isDistrictScope || !userDistrictId) return null;
    const row = districts.find((d) => Number(d.district_id) === userDistrictId);
    return row?.district_name || `District #${userDistrictId}`;
  }, [districts, isDistrictScope, userDistrictId]);

  const departmentName = useMemo(() => {
    if (!isDepartmentScope || !userDepartmentId) return null;
    const row = departments.find((d) => Number(d.department_id) === userDepartmentId);
    return row?.department_name || `Department #${userDepartmentId}`;
  }, [departments, isDepartmentScope, userDepartmentId]);

  const enriched = useMemo(() => applications.map((a) => {
    const tank = tankById.get(Number(a.tank_id));
    const district = districtById.get(Number(a.district_id));
    const department = departmentById.get(Number(a.department_id));
    return {
      ...a,
      id: `NOC${String(a.application_id).padStart(3, '0')}`,
      tankName: tank?.water_body_name || `Tank #${a.tank_id}`,
      districtName: district?.district_name || tank?.district_name || '-',
      blockName: tank?.block_name || '-',
      departmentName: department?.department_name || tank?.owned_by || '-',
      statusUpper: String(a.status || '').toUpperCase(),
    };
  }), [applications, tankById, districtById, departmentById]);

  const filtered = useMemo(() => enriched.filter((r) => {
    if (filterDistrict && r.districtName !== filterDistrict) return false;
    if (filterDepartment && r.departmentName !== filterDepartment) return false;
    if (filterStatus) {
      if (filterStatus === 'APPROVED') {
        if (r.statusUpper !== 'APPROVED' && r.statusUpper !== 'DEEMED_APPROVED') return false;
      } else if (r.statusUpper !== filterStatus) return false;
    }
    if (filterCompleted === 'yes' && !r.completion_issued_at) return false;
    if (filterCompleted === 'no'  &&  r.completion_issued_at) return false;
    return true;
  }), [enriched, filterDistrict, filterDepartment, filterStatus, filterCompleted]);

  const stats = useMemo(() => {
    const acc = { total: 0, approved: 0, pending: 0, rejected: 0, returned: 0, draft: 0, covered: 0, restored: 0 };
    for (const r of enriched) {
      const s = r.statusUpper;
      if (s !== 'DRAFT') acc.total++; // "NOC Applications" = submitted only
      if (s === 'APPROVED' || s === 'DEEMED_APPROVED') {
        acc.approved++;
        acc.covered++;
        if (r.completion_issued_at) acc.restored++;
      }
      else if (s === 'REJECTED') acc.rejected++;
      else if (s === 'RETURNED') acc.returned++;
      else if (s === 'DRAFT')    acc.draft++;
      else acc.pending++;
    }
    return acc;
  }, [enriched]);

  const statusPieData = useMemo(() => [
    { name: 'Approved', value: stats.approved, color: '#10b981' },
    { name: 'Pending',  value: stats.pending,  color: '#f97316' },
    { name: 'Returned', value: stats.returned, color: '#f59e0b' },
    { name: 'Rejected', value: stats.rejected, color: '#ef4444' },
    { name: 'Draft',    value: stats.draft,    color: '#9ca3af' },
  ].filter((s) => s.value > 0), [stats]);

  const groupedBy = (keyFn) => {
    const map = new Map();
    for (const r of enriched) {
      if (r.statusUpper === 'DRAFT') continue; // drafts aren't submitted — keep rows adding up
      const k = keyFn(r) || '-';
      const cur = map.get(k) || { name: k, total: 0, approved: 0, pending: 0, rejected: 0, returned: 0 };
      cur.total++;
      const s = r.statusUpper;
      if (s === 'APPROVED' || s === 'DEEMED_APPROVED') cur.approved++;
      else if (s === 'REJECTED') cur.rejected++;
      else if (s === 'RETURNED') cur.returned++;
      else cur.pending++;
      map.set(k, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };
  const byDistrict   = useMemo(() => groupedBy((r) => r.districtName),   [enriched]);
  const byDepartment = useMemo(() => groupedBy((r) => r.departmentName), [enriched]);

  const clearFilters = () => {
    setFilterDistrict(''); setFilterDepartment(''); setFilterStatus(''); setFilterCompleted('');
  };

  // Open the drill-down modal for a KPI card.
  const openDrill = (title, predicate) => {
    setDrillModal({ title, rows: enriched.filter(predicate) });
  };
  const isApprovedRow  = (r) => r.statusUpper === 'APPROVED' || r.statusUpper === 'DEEMED_APPROVED';
  const isPendingRow   = (r) => !['APPROVED','DEEMED_APPROVED','REJECTED','RETURNED','DRAFT'].includes(r.statusUpper);
  const isRestoredRow  = (r) => isApprovedRow(r) && !!r.completion_issued_at;

  const statusPill = (s) => {
    const u = String(s || '').toUpperCase();
    let cls = 'bg-slate-100 text-slate-700 border-slate-400';
    if (u === 'APPROVED' || u === 'DEEMED_APPROVED') cls = 'bg-emerald-100 text-emerald-800 border-emerald-600';
    else if (u === 'REJECTED') cls = 'bg-rose-100 text-rose-800 border-rose-600';
    else if (u === 'RETURNED') cls = 'bg-amber-100 text-amber-800 border-amber-500';
    else if (u === 'PENDING')  cls = 'bg-orange-100 text-orange-800 border-orange-500';
    return <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold border-l-4 ${cls}`}>{u || 'PENDING'}</span>;
  };

  return (
    <div className="bg-slate-100 min-h-screen pt-16 sm:pt-20">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b-4 border-brand-accent">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-800 mr-3">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-500">Tamil Nadu NOC Portal</span>
            <span className="text-gray-300 mx-2">/</span>
            <span className="text-brand-700 font-medium text-sm">
              {isDistrictScope
                ? `District Dashboard${districtName ? ` — ${districtName}` : ''}`
                : isDepartmentScope
                  ? `Department Dashboard${departmentName ? ` — ${departmentName}` : ''}`
                  : 'State Dashboard'}
            </span>
          </div>
          {isDepartmentScope && (
            <button
              onClick={() => navigate('/admin/department-users')}
              className="bg-white border border-brand-100 text-brand-800 hover:bg-brand-50 px-4 py-2 font-medium text-sm transition-all"
              title="Manage officers for your department"
            >
              Department Users
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {/* Page header */}
        <div className="bg-brand-800 border-l-8 border-brand-accent shadow-sm p-5 sm:p-6 text-white">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">
            {isDistrictScope
              ? `District Monitoring — ${districtName || '—'}`
              : isDepartmentScope
                ? `Department Monitoring — ${departmentName || '—'}`
                : 'State-Level NOC Monitoring'}
          </h1>
          <p className="text-sm text-brand-100">
            {isDistrictScope
              ? 'For the District Collector — real-time NOC activity within your district.'
              : isDepartmentScope
                ? 'For the Department Admin — aggregate activity across every district under your department.'
                : 'For the Secretary / Administrator — aggregate activity across every district and department.'}
          </p>
        </div>

        {loading ? (
          <div className="bg-white shadow p-12 text-center text-gray-500">Loading dashboard…</div>
        ) : (
          <>
            {/* KPI cards. Every card is clickable — opens a modal with the
                matching applications. "Approved" was removed because it
                duplicated "NOC Issued (Covered)". */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <KpiCard color="indigo"  Icon={FileText}    label="NOC Applications"
                value={stats.total}
                onClick={() => openDrill('All NOC Applications', (r) => r.statusUpper !== 'DRAFT')}
                hint="All submitted applications — click to view" />
              <KpiCard color="orange"  Icon={Clock}       label="Awaiting Approval"
                value={stats.pending}
                onClick={() => openDrill('Awaiting Approval', isPendingRow)}
                hint="Pending action by an officer — click to view" />
              <KpiCard color="amber"   Icon={AlertCircle} label="Returned for Clarification"
                value={stats.returned}
                onClick={() => openDrill('Returned for Clarification', (r) => r.statusUpper === 'RETURNED')}
                hint="Sent back to Organization — click to view" />
              <KpiCard color="red"     Icon={XCircle}     label="Rejected"
                value={stats.rejected}
                onClick={() => openDrill('Rejected Applications', (r) => r.statusUpper === 'REJECTED')}
                hint="Rejected at any level — click to view" />
              <KpiCard color="sky"     Icon={FileCheck}   label="NOC Issued (Covered)"
                value={stats.covered}
                onClick={() => openDrill('NOC Issued — Covered Tanks', isApprovedRow)}
                hint="NOC approved by all levels — click to view" />
              <KpiCard color="teal"    Icon={Award}       label="Work Completed (Restored)"
                value={stats.restored}
                onClick={() => openDrill('Work Completed — Restored Tanks', isRestoredRow)}
                hint="Completion certificate issued — click to view" />
            </div>

            {/* Charts row 1: Status distribution + Applications by District
                side-by-side (per client request 2026-09-24). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status donut */}
              <div className="bg-white shadow-sm border border-slate-200 border-t-4 border-t-sky-500 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-brand-700" /> Status distribution
                  </h3>
                </div>
                {statusPieData.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">
                    No data
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        label={(e) => `${e.name}: ${e.value}`}
                      >
                        {statusPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Applications by District (state scope only) */}
              {!isDistrictScope && (
                <div className="bg-white shadow-sm border border-slate-200 border-t-4 border-t-emerald-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-brand-700" /> Applications by District (top 12)
                  </h3>
                  {byDistrict.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={byDistrict.slice(0, 12)} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                        <CartesianGrid stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="approved" stackId="a" name="Approved" fill="#10b981" />
                        <Bar dataKey="pending"  stackId="a" name="Pending"  fill="#f97316" />
                        <Bar dataKey="returned" stackId="a" name="Returned" fill="#f59e0b" />
                        <Bar dataKey="rejected" stackId="a" name="Rejected" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>

            {/* Applications by Department (hidden when scope is a single dept) */}
            {!isDepartmentScope && (
              <div className="bg-white shadow-sm border border-slate-200 border-t-4 border-t-violet-500 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-brand-700" /> Applications by Department
                </h3>
                {byDepartment.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={byDepartment.slice(0, 8)} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="approved" stackId="a" name="Approved" fill="#10b981" />
                      <Bar dataKey="pending"  stackId="a" name="Pending"  fill="#f97316" />
                      <Bar dataKey="returned" stackId="a" name="Returned" fill="#f59e0b" />
                      <Bar dataKey="rejected" stackId="a" name="Rejected" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Filtered list */}
            <div id="apps-table" className="bg-white shadow-sm border border-gray-200 overflow-hidden scroll-mt-20">
              <div className="px-4 py-3 bg-brand-800 flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold text-white flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-brand-accent" />
                  Applications ({filtered.length})
                </span>
                <div className="flex items-center gap-2">
                  {!isDistrictScope && (
                    <select
                      value={filterDistrict}
                      onChange={(e) => setFilterDistrict(e.target.value)}
                      className="text-xs border border-gray-300 px-2 py-1"
                    >
                      <option value="">All Districts</option>
                      {byDistrict.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                    </select>
                  )}
                  {!isDepartmentScope && (
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="text-xs border border-gray-300 px-2 py-1"
                    >
                      <option value="">All Departments</option>
                      {byDepartment.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                    </select>
                  )}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-xs border border-gray-300 px-2 py-1"
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved (Covered)</option>
                    <option value="RETURNED">Returned</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="DRAFT">Draft</option>
                  </select>
                  <select
                    value={filterCompleted}
                    onChange={(e) => setFilterCompleted(e.target.value)}
                    className="text-xs border border-gray-300 px-2 py-1"
                    title="Whether the Work Completion Certificate has been issued"
                  >
                    <option value="">All Completion</option>
                    <option value="yes">Restored (cert issued)</option>
                    <option value="no">Not yet restored</option>
                  </select>
                  {(filterDistrict || filterDepartment || filterStatus || filterCompleted) && (
                    <button onClick={clearFilters} className="text-xs text-brand-100 hover:text-white hover:underline">Clear</button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-brand-600 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">NOC ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Tank</th>
                      {!isDistrictScope && <th className="px-3 py-2 text-left text-xs font-semibold uppercase">District</th>}
                      {!isDepartmentScope && <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Department</th>}
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Submitted</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Completion Cert</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5 + (isDistrictScope ? 0 : 1) + (isDepartmentScope ? 0 : 1)}
                          className="px-3 py-8 text-center text-gray-500"
                        >
                          No applications match the current filters.
                        </td>
                      </tr>
                    ) : filtered.slice(0, 100).map((r) => (
                      <tr
                        key={r.application_id}
                        className="odd:bg-white even:bg-brand-50 hover:bg-brand-100 cursor-pointer"
                        onClick={() => navigate(`/workflow/${r.id}`)}
                        title="Open workflow"
                      >
                        <td className="px-3 py-2 font-semibold text-brand-700">{r.id}</td>
                        <td className="px-3 py-2">{r.tankName}</td>
                        {!isDistrictScope && <td className="px-3 py-2">{r.districtName}</td>}
                        {!isDepartmentScope && <td className="px-3 py-2">{r.departmentName}</td>}
                        <td className="px-3 py-2">{statusPill(r.status)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.completion_issued_at
                            ? <span className="text-teal-700 font-medium">{new Date(r.completion_issued_at).toLocaleDateString('en-IN')}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length > 100 && (
                  <p className="text-xs text-center text-gray-500 p-2">
                    Showing first 100 of {filtered.length}. Use the filters above to narrow down.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* KPI drill-down modal — opens when any KPI card is clicked, lists
          the matching applications. Clicking a NOC ID opens the full
          workflow / application detail page. */}
      {drillModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setDrillModal(null)}
        >
          <div
            className="bg-white shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 bg-brand-800 border-b-4 border-brand-accent text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{drillModal.title}</h3>
                <p className="text-xs text-brand-100 mt-0.5">
                  {drillModal.rows.length} application{drillModal.rows.length === 1 ? '' : 's'}
                  {' '}— click a NOC ID to open full details.
                </p>
              </div>
              <button
                onClick={() => setDrillModal(null)}
                className="p-1.5 hover:bg-white/20"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {drillModal.rows.length === 0 ? (
                <div className="p-10 text-center text-gray-500 text-sm">No applications in this category.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-brand-600 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">NOC ID</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">Tank</th>
                      {!isDistrictScope && <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">District</th>}
                      {!isDepartmentScope && <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">Department</th>}
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">Submitted</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-white">Completion Cert</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {drillModal.rows.map((r) => (
                      <tr key={r.application_id} className="odd:bg-white even:bg-brand-50 hover:bg-brand-100">
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => { setDrillModal(null); navigate(`/workflow/${r.id}`); }}
                            className="font-semibold text-brand-700 hover:text-brand-900 hover:underline"
                            title="Open full application details"
                          >
                            {r.id}
                          </button>
                        </td>
                        <td className="px-4 py-2">{r.tankName}</td>
                        {!isDistrictScope && <td className="px-4 py-2">{r.districtName}</td>}
                        {!isDepartmentScope && <td className="px-4 py-2">{r.departmentName}</td>}
                        <td className="px-4 py-2">{statusPill(r.status)}</td>
                        <td className="px-4 py-2 text-xs text-gray-600">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {r.completion_issued_at
                            ? <span className="text-teal-700 font-medium">{new Date(r.completion_issued_at).toLocaleDateString('en-IN')}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ color, Icon, label, value, onClick, hint }) => {
  const palette = {
    indigo:  'bg-brand-700 border-brand-900',
    emerald: 'bg-emerald-600 border-emerald-800',
    orange:  'bg-orange-500 border-orange-700',
    amber:   'bg-amber-500 border-amber-700',
    red:     'bg-rose-600 border-rose-800',
    sky:     'bg-sky-600 border-sky-800',
    teal:    'bg-teal-600 border-teal-800',
  };
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  const clickClass = clickable ? 'hover:brightness-110 hover:shadow-md transition text-left' : '';
  return (
    <Wrapper
      onClick={onClick}
      title={hint}
      className={`${palette[color]} border-b-4 p-4 text-white shadow-sm ${clickClass}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase opacity-90 tracking-wider mb-1">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold leading-tight">{value}</p>
        </div>
        <div className="w-9 h-9 bg-white/20 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Wrapper>
  );
};
